// src/pages/GroomerDetails.js

import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Toolbar as MuiToolbar,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ServicesTable from "../components/ServicesTable";
import Pagination from "../components/Pagination";
import ImageGalleryDropdown from "../components/ImageGalleryDropdown";
import { useServicesState } from "../hooks/useServicesState";
import { ACTIONS } from '../reducers/listActions';
import { IconButton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import RecordNavigator from "../components/RecordNavigator";
import { formatPhoneNumber } from "../utils/phoneUtils";

const groomerServicesColumns = [
  { id: "redirect", label: "", sortable: false },
  { id: "date", label: "Date", sortable: true },
  { id: "time", label: "Time", sortable: false },
  { id: "service", label: "Service", sortable: false },
  { id: "amount", label: "Amount", sortable: false },
  { id: "customer", label: "Customer", sortable: false },
  { id: "pet", label: "Pet", sortable: false },
  { id: "comments", label: "", sortable: false },
  { id: "imageUrl", label: "", sortable: false },
];

const GroomerDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const groomers = location.state?.records || [];
  const navigate = useNavigate();
  const [groomer, setGroomer] = useState(null);
  const { state, dispatch, setPage, setRowsPerPage, handleSortRequest } = useServicesState();
  const { services, order, orderBy, page, rowsPerPage, totalServices } = state;
  const [editedGroomer, setEditedGroomer] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showServices, setShowServices] = useState(false);

  useEffect(() => {
    async function fetchGroomerDetails() {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");
        const response = await api.get(`/groomers/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const groomerData = response.data;
        setGroomer(groomerData);
        setEditedGroomer({
          ...groomerData,
          active: groomerData.active !== undefined ? groomerData.active : false,
        });
      } catch (error) {
        console.error("Error fetching groomer details:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchGroomerDetails();
  }, [id]);

  useEffect(() => {
    if (groomer) {
      setEditedGroomer(groomer);
    }
  }, [groomer]);

  // Fetch groomer services with API-side pagination and sorting
  useEffect(() => {
    if (!showServices) return;

    async function fetchGroomerServices() {
      dispatch({ type: ACTIONS.FETCH_INIT });

      try {
        const token = localStorage.getItem("token");
        const response = await api.get(`/groomers/${id}/services`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: state.page + 1,
            limit: state.rowsPerPage,
            sortBy: state.orderBy,
            order: state.order,
          },
        });
        dispatch({
          type: ACTIONS.FETCH_SUCCESS,
          payload: {
            services: response.data.data,
            total: response.data.total,
          },
        });
      } catch (error) {
        dispatch({ type: ACTIONS.FETCH_FAILURE, payload: "Failed to fetch services." });
      }
    }
    fetchGroomerServices();
  }, [id, showServices, state.page, state.rowsPerPage, state.order, state.orderBy, dispatch]);

  if (loading) {
    return <Typography>Loading groomer details...</Typography>;
  }

  if (!groomer) {
    return <Typography>Groomer not found.</Typography>;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleServices = () => setShowServices(prev => !prev);

  if (loading) {
    return <Typography>Loading groomer details...</Typography>;
  }

  if (!groomer) {
    return <Typography>Groomer not found.</Typography>;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedGroomer({ ...editedGroomer, [name]: value });
  };

  const handleStatusChange = (e) => {
    setEditedGroomer({ ...editedGroomer, active: e.target.checked });
  };

  const handleEditClick = async () => {
    if (isEditing) {
      try {
        const token = localStorage.getItem("token");
        const updatePayload = {
          firstName: editedGroomer.firstName,
          lastName: editedGroomer.lastName,
          address: editedGroomer.address,
          phone1: editedGroomer.phone1,
          phone2: editedGroomer.phone2,
          email: editedGroomer.email,
          active: editedGroomer.active, // boolean
          comments: editedGroomer.comments,
        };
        //console.log("Sending update payload:", updatePayload);
        const response = await api.put(`/groomers/${id}`, updatePayload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data) {
          setGroomer(prev => ({
            ...response.data,
            bucket: prev.bucket || response.data.bucket,
          }));

          setEditedGroomer(prev => ({
            ...response.data,
            bucket: prev.bucket || response.data.bucket,
          }));

          setIsEditing(false);
        } else {
          console.error("No updated data received from API.");
        }
      } catch (error) {
        console.error("Error saving changes:", error.response ? error.response.data : error);
      }
    } else {
      setIsEditing(true);
    }
  };

  const handleDeleteClick = () => {
    if (window.confirm("Are you sure you want to delete this groomer? This action cannot be undone.")) {
      const token = localStorage.getItem("token");
      api
        .delete(`/groomers/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          navigate("/groomers");
        })
        .catch((error) => {
          console.error("Error deleting groomer:", error);
        });
    }
  };

  const disabledInputStyles = {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "black",
    },
  };

  return (
    <>
      <MuiToolbar />
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Paper sx={{ p: 2 }}>
          {/* Header */}
          <Typography variant="h5" gutterBottom sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Groomer Details</span>
            <RecordNavigator records={groomers} currentId={id} basePath="/groomers" />
          </Typography>

          {/* Groomer Details Section */}
          <Box sx={{ maxWidth: '80%', mx: 'auto' }}>
            <Grid container spacing={2} justifyContent="center">
              {/* Row 1: First Name, Last Name, Address */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={editedGroomer.firstName || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={editedGroomer.lastName || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Address"
                  name="address"
                  value={editedGroomer.address || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              {/* Row 2: Phone 1, Phone 2, Email */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Phone 1"
                  name="phone1"
                  value={formatPhoneNumber(editedGroomer.phone1) || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Phone 2"
                  name="phone2"
                  value={formatPhoneNumber(editedGroomer.phone2) || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  value={editedGroomer.email || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              {/* Row 3: Status, Comments, Photo Upload */}
              <Grid item xs={12} sm={2} sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editedGroomer.active || false}
                      onChange={handleStatusChange}
                      disabled={!isEditing}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Comments"
                  name="comments"
                  value={editedGroomer.comments || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  multiline
                  rows={2}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={2} sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ImageGalleryDropdown
                  images={editedGroomer.bucket?.images || []}
                  onUploadSuccess={(newImageData) => {
                    setEditedGroomer(prev => ({
                      ...prev,
                      bucket: {
                        ...prev.bucket,
                        images: [...(prev.bucket?.images || []), newImageData]
                      }
                    }));
                  }}
                  onDelete={(imageId) => {
                    api.delete(`/images/${imageId}`)
                      .then(() => {
                        setEditedGroomer(prev => ({
                          ...prev,
                          bucket: {
                            ...prev.bucket,
                            images: prev.bucket.images.filter(img => img.id !== imageId)
                          }
                        }));
                      })
                      .catch(error => {
                        console.error("Error deleting image:", error);
                      });
                  }}
                  groomerId={groomer.id}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', maxWidth: '60%', mx: 'auto' }}>
            <Button variant="contained" color="primary" onClick={handleEditClick}>
              {isEditing ? "Save Changes" : "Edit Groomer"}
            </Button>
            <Button variant="outlined" color="error" onClick={handleDeleteClick}>
              Delete Groomer
            </Button>
          </Box>
        </Paper>

        {/* Services Table Panel */}
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={toggleServices} size="small">
              {showServices ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Typography variant="h6">
              Services for {groomer.firstName} {groomer.lastName}
            </Typography>
          </Box>
          {showServices && (
            <>
              {services.length > 0 ? (
                <>
                  {totalServices > rowsPerPage && (
                    <Pagination
                      count={totalServices}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                  <ServicesTable
                    data={services}
                    columns={groomerServicesColumns}
                    order={order}
                    orderBy={orderBy}
                    onSortRequest={handleSortRequest}
                  />
                  {totalServices > rowsPerPage && (
                    <Pagination
                      count={totalServices}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </>
              ) : (
                <Typography>No services found.</Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default GroomerDetails;